import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import useFetch, { Status } from '../../../../../hooks/fetch.hook';
import Spinner from '../../../../Shared/Spinner';
import Alert from '../../../../Shared/Alert';
import { AccountApiModel } from './models/account.api-model';
import type { IAccount } from '../../../../../models/pa-egna-ben/account.model';
import { TranslationService } from '../../../../../services/translation.service';
import FocusTrap from '../../../../Shared/FocusTrap';
import InformationBox from '../../../Shared/InformationBox';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import checkCircleGreenIcon from '../../../../../../assets/icons/check-circle-filled-green-600.svg';
import { InformationBoxType } from '../../../Shared/InformationBox/information-box-type.enum';
import AntiforgeryService from '../../../../../services/anti-forgery.service';

function processReceivedData(data: AccountApiModel[]): IAccount[] {
  return data?.map((d) => AccountApiModel.mapToBusinessModel(d)) ?? [];
}

function Accounts() {
  // === STATE ===
  const [getAccountsResult, setGetAccountsResult] = useFetch<AccountApiModel[]>('/api/paegnaben/admin/accounts');
  const [deleteAccountStatus, setDeleteAccountStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });
  const [selectedAccount, setSelectedAccount] = useState<IAccount | undefined>(undefined);

  // === VARIABLES ===
  const accounts = getAccountsResult.data ? processReceivedData(getAccountsResult.data) : undefined;

  // === EVENT HANDLERS ===
  async function handleConfirmDeleteClick() {
    if (!selectedAccount) return;

    setDeleteAccountStatus({ status: Status.notperformed, statusCode: 0 });
    const response = await fetch(`/api/paegnaben/admin/accounts/${selectedAccount?.scimId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
    });

    setDeleteAccountStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
    if (response.ok) {
      setGetAccountsResult((ps) => ({ ...ps, data: ps.data ? ps.data.filter((a) => a.scimId !== selectedAccount.scimId) : [] }));
    }

    setSelectedAccount(undefined);
  }

  function handleOnClose() {
    setSelectedAccount(undefined);
  }

  // === RENDER ===
  if ((getAccountsResult.status === Status.failed && getAccountsResult.httpResponseStatusCode === 401) || deleteAccountStatus.statusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if ((getAccountsResult.status === Status.failed && getAccountsResult.httpResponseStatusCode === 403) || deleteAccountStatus.statusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <>
      {selectedAccount && (
        <FocusTrap isVisible={!!selectedAccount}>
          <div className="modal d-block fade show" aria-labelledby="sign-modal-title" aria-modal="true" role="dialog">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h2 id="sign-modal-title" className="modal-title fs-5">
                    Ta bort användare
                  </h2>
                  <button type="button" className="btn-close" aria-label={TranslationService.get('common.close')} onClick={handleOnClose}></button>
                </div>
                <div className="modal-body pt-0">
                  Vill du verkligen ta bort användaren{' '}
                  <strong>
                    {selectedAccount.firstName} {selectedAccount.lastName}
                  </strong>{' '}
                  med e-postadress <strong>{selectedAccount.emailAddress}</strong>?
                </div>
                <div className="modal-footer pt-0">
                  <button type="button" className="btn btn-secondary" onClick={handleOnClose}>
                    Avbryt
                  </button>
                  <button type="button" className="btn btn-danger" onClick={handleConfirmDeleteClick}>
                    Ta bort
                  </button>
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
      {selectedAccount && <div className="modal-backdrop fade show"></div>}
      <div>
        <h1>Användare</h1>
        {getAccountsResult.status === Status.loading && <Spinner className="my-4 my-md-5" />}
        {getAccountsResult.status === Status.failed && <Alert show={true} heading="Kunde inte hämta användare" closable={false} className="mb-4" />}
        {deleteAccountStatus.status === Status.failed && <Alert show={true} heading="Kunde inte ta bort användaren" closable={false} className="mb-4" />}
        {deleteAccountStatus.status === Status.success && <InformationBox type={InformationBoxType.Success} headingElement="Användaren har tagits bort" className="mb-4" setFocus={true} />}
        {getAccountsResult.status === Status.success && (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Förnamn</th>
                  <th scope="col">Efternamn</th>
                  <th scope="col">E-postadress</th>
                  <th scope="col">Skapad</th>
                  <th scope="col" className="text-nowrap">
                    Loggat in
                  </th>
                  <th scope="col">Klasser</th>
                  <th scope="col">
                    <span className="visually-hidden">Åtgärder</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts?.map((account) => (
                  <tr key={account.scimId} className="align-middle">
                    <td>{account.firstName}</td>
                    <td>{account.lastName}</td>
                    <td>{account.emailAddress}</td>
                    <td className="text-nowrap">{account.createdAt?.toLocaleString()}</td>
                    <td className="text-center">{account.user ? <img alt="Ja" src={DataUrlHelper.encode(checkCircleGreenIcon)} /> : <span className="visually-hidden">Nej</span>}</td>
                    <td className="text-center">{account.user && account.participations ? account.participations.length : ''}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-tertiary"
                        onClick={() => {
                          setSelectedAccount(account);
                        }}
                      >
                        <span className="text-nowrap">Ta bort</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default Accounts;